/**
 * @fileoverview OrderDataAnalysisService
 * Service for analyzing orden_produccion internal table relationships
 * and validating non-empty data that requires specific processes
 */

import { pb } from '../helpers/pocketbase_helper';

/**
 * Service for analyzing production order data
 */
export class OrderDataAnalysisService {
  /**
   * Mapeo de campos de orden_produccion a nombres de colecciones
   */
  static FIELD_TO_COLLECTION_MAP = {
    'corte_conversion': 'corte_conversion',
    'brillo_interno': 'parametros_brillo_interno',
    'pegue_interno': 'parametros_pegue_interno', 
    'plastificado_interno': 'parametros_plastificado_interno',
    'troquelado_interno': 'parametros_troquelado_interno',
    'acabado_interno': 'parametros_acabado_interno',
    'recubrimiento': 'recubrimiento_tipo_recubrimiento'
  };

  /**
   * Internal table relationship fields to analyze
   */
  static INTERNAL_RELATIONSHIP_FIELDS = [
    'corte_conversion',
    'recubrimiento',
    'brillo_interno',
    'pegue_interno',
    'plastificado_interno',
    'troquelado_interno',
    'acabado_interno'
  ];

  /**
   * Analyzes order data to identify non-empty internal relationships
   * @param {string} orderId - ID of the production order
   * @returns {Promise<Object>} Analysis result with internal relationships
   */
  static async analyzeOrderData(orderId) {
    if (!orderId) {
      throw new Error('ID de orden es requerido');
    }

    try {
      // Fetch order data with internal relationships and nested relations
      const expandFields = [
        ...this.INTERNAL_RELATIONSHIP_FIELDS,
        'corte_conversion',
        'corte_conversion.parametro',
        'corte_conversion.parametro.impresion'
      ].join(',');
      
      const order = await pb.collection('orden_produccion').getOne(orderId, {
        expand: expandFields
      });


      // Extract internal relationships
      const internalRelationships = this.getInternalRelationships(order);
      
      const result = {
        orderId,
        hasInternalData: internalRelationships.length > 0,
        internalRelationships,
        requiredProcesses: [] // Will be populated by ProcessMappingService
      };
      
      return result;
    } catch (error) {
      throw new Error(`Error al analizar datos de la orden: ${error.message}`);
    }
  }

  /**
   * Validates if order has non-empty internal data
   * @param {string} orderId - ID of the production order
   * @returns {Promise<boolean>} True if order has internal data
   */
  static async validateInternalData(orderId) {
    if (!orderId) {
      throw new Error('ID de orden es requerido');
    }

    try {
      const expandFields = [
        ...this.INTERNAL_RELATIONSHIP_FIELDS,
        'corte_conversion',
        'corte_conversion.parametro',
        'corte_conversion.parametro.impresion'
      ].join(',');
      
      const order = await pb.collection('orden_produccion').getOne(orderId, {
        expand: expandFields
      });

      const internalRelationships = this.getInternalRelationships(order);
      return internalRelationships.length > 0;
    } catch (error) {
      throw new Error(`Error al validar datos internos de la orden: ${error.message}`);
    }
  }

  /**
   * Extracts non-empty internal relationships from order data
   * @param {Object} orderData - Order data object
   * @returns {Array} Array of internal relationships with data
   */
  static getInternalRelationships(orderData) {
    const relationships = [];

    this.INTERNAL_RELATIONSHIP_FIELDS.forEach(fieldName => {
      const fieldData = orderData[fieldName];
      
      // Check if field has non-empty data
      if (this.hasValidData(fieldData)) {
        // Get the collection name from the field name
        const collectionName = this.FIELD_TO_COLLECTION_MAP[fieldName] || fieldName;
        
        relationships.push({
          tableName: collectionName, // Use collection name instead of field name
          fieldName: fieldName, // Keep original field name for reference
          data: fieldData
        });

        // Special handling for corte_conversion - check for nested impresion
        if (fieldName === 'corte_conversion' && Array.isArray(fieldData)) {
          let hasImpresionData = false;
          
          // Get the expanded data from orderData.expand.corte_conversion
          const expandedCorteData = orderData.expand?.corte_conversion;
          
          if (expandedCorteData && Array.isArray(expandedCorteData)) {
            expandedCorteData.forEach((corteItem) => {
              // Check if this corte item has parametros with impresion data
              if (corteItem.expand?.parametro && Array.isArray(corteItem.expand.parametro)) {
                corteItem.expand.parametro.forEach((parametro) => {
                  // Check if this parametro has impresion data
                  if (parametro.impresion) {
                    // Check if the impresion is expanded and has meaningful data
                    if (parametro.expand?.impresion) {
                      // Validate if impresion has meaningful data (impresos, sobrantes, or pasadas > 0)
                      if (this.hasValidImpresionData(parametro.expand.impresion)) {
                        hasImpresionData = true;
                      }
                    } else {
                      hasImpresionData = true; // Still consider it as having impresion
                    }
                  }
                });
              }
            });
          }
          
          // If we found any impresion data, add it as a required process
          if (hasImpresionData) {
            relationships.push({
              tableName: 'impresion',
              fieldName: 'impresion',
              parentField: 'corte_conversion',
              data: ['impresion_data'] // Placeholder data since we found impresion
            });
          }
        }
      }
    });

    return relationships;
  }

  /**
   * Checks if field data is valid (non-empty)
   * @param {*} data - Field data to validate
   * @returns {boolean} True if data is valid and non-empty
   */
  static hasValidData(data) {
    if (data === null || data === undefined) {
      return false;
    }
    
    if (Array.isArray(data)) {
      return data.length > 0;
    }
    
    if (typeof data === 'string') {
      return data.trim().length > 0;
    }
    
    return Boolean(data);
  }

  /**
   * Checks if impresion data has meaningful values (impresos, sobrantes, pasadas > 0)
   * @param {Object} impresionParam - Impresion parameter data
   * @returns {boolean} True if impresion has meaningful data
   */
  static hasValidImpresionData(impresionParam) {
    if (!impresionParam || typeof impresionParam !== 'object') {
      return false;
    }

    // Check for meaningful impresion data - focus on impresos, sobrantes, pasadas
    const impresos = impresionParam.impresos;
    const sobrantes = impresionParam.sobrantes;
    const pasadas = impresionParam.pasadas;

    // At least one of these should be greater than 0
    const hasValidData = (impresos && impresos > 0) || 
                        (sobrantes && sobrantes > 0) || 
                        (pasadas && pasadas > 0);
    
    return hasValidData;
  }

  /**
   * Gets the list of internal relationship field names
   * @returns {Array<string>} List of field names
   */
  static getInternalRelationshipFields() {
    return [...this.INTERNAL_RELATIONSHIP_FIELDS];
  }
}
